import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium'
import { useCallback } from 'react'
import { formatAmount } from '../../../tiger/libs/Common'
import { bankName, FAILURE_REASON_LABELS, formatInstant, PENDING_REASON_LABELS, type PisDeposit, reasonLabel } from '../types'
import { PisDepositDetailPanel } from './PisDepositDetailPanel'
import { PisDepositStatusChip } from './PisDepositStatusChip'

type PisDepositsTableProps = {
  customerId: string
  deposits: PisDeposit[]
  isLoading: boolean
}

const columns: GridColDef<PisDeposit>[] = [
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 150,
    flex: 0.8,
    valueFormatter: (value) => formatInstant(value) ?? '-',
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 110,
    flex: 0.6,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value) => (value ? formatAmount(value) : '-'),
    sortComparator: (a, b) => (a?.value ?? 0) - (b?.value ?? 0),
  },
  {
    field: 'bank',
    headerName: 'Bank',
    minWidth: 130,
    flex: 0.7,
    valueGetter: (_value, row) => bankName(row.bank) ?? '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 110,
    flex: 0.5,
    renderCell: ({ value }) => <PisDepositStatusChip status={value} />,
  },
  {
    field: 'reason',
    headerName: 'Reason',
    minWidth: 220,
    flex: 1.2,
    valueGetter: (_value, row) =>
      reasonLabel(row.failureReason, FAILURE_REASON_LABELS) ?? reasonLabel(row.pendingReason, PENDING_REASON_LABELS) ?? '',
  },
  { field: 'paymentReference', headerName: 'OCR', minWidth: 120, flex: 0.6 },
]

export const PisDepositsTable = ({ customerId, deposits, isLoading }: PisDepositsTableProps) => {
  const getDetailPanelContent = useCallback(
    ({ row }: { row: PisDeposit }) => <PisDepositDetailPanel customerId={customerId} deposit={row} />,
    [customerId],
  )
  const getDetailPanelHeight = useCallback(() => 'auto' as const, [])

  return (
    <Box sx={{ maxWidth: '93vw' }}>
      <DataGridPremium
        rows={deposits}
        columns={columns}
        loading={isLoading}
        autoHeight
        pagination
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.depositId ?? ''}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row')}
        slots={{
          noRowsOverlay: () => (
            <Stack
              sx={{
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              No direct deposits found
            </Stack>
          ),
        }}
        sx={{
          '& .odd-row': { backgroundColor: 'action.hover' },
          '& .MuiDataGrid-row': { borderBottom: '1px solid', borderColor: 'divider' },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
            fontSize: 13,
          },
        }}
      />
    </Box>
  )
}
